import { safeInject } from '@wui/common-library'
import throttle from 'lodash.throttle'
import { Emitter } from 'mitt'
import { DeepReadonly, nextTick, onScopeDispose, readonly, Ref, ref } from 'vue'

import { Events } from '@/shared/types/app'

import { findTopVisibleAnchor } from './find-top-visible-anchor'

type ScrollToBottomOptions = {
  stick?: boolean
  measure?: boolean
  force?: boolean
  /**
   * Если false — один проход скролла и сразу resolve.
   * stick при этом всё равно включается и дожимает позицию по @resize,
   * пока virtual scroller уточняет высоты.
   */
  settle?: boolean
}

type UseChatScrollOptions = {
  /**
   * When false, do not stick to the list bottom or emit userAtChatBottom.
   * Used for anchored history windows where the list end is not the chat tail yet.
   */
  canStickToBottom?: () => boolean
}

type ReusedViewportSnapshot = {
  scrollOffset: number
  /** True when the user left the chat pinned to the newest messages. */
  wasAtBottom: boolean
  anchorEventId?: number
  anchorIndex?: number
}

type ScrollMetrics = {
  scrollOffset: number
  scrollSize: number
  viewportSize: number
}

type ScrollToItemAlign = {
  align?: 'start' | 'center' | 'end' | 'auto'
}

type ScrollToItemOptions = {
  forceAttempts?: boolean
  /**
   * For unread anchors: if content from the item to the list end fits in the
   * viewport, pin to the bottom instead of forcing the item to the top.
   * Re-evaluated while sizes settle after measure.
   */
  bottomIfFits?: boolean
  align?: ScrollToItemAlign['align']
}

/** Runtime shape of VirtualScroller component ref (expose unwraps Ref fields). */
type ScrollerInstance = {
  $el?: HTMLElement
  scrollToItem?: (index: number, options?: ScrollToItemAlign) => void
  scrollToBottom?: () => void
  scrollTo?: (offset: number) => void
  getItemOffset?: (index: number) => number
  scrollOffset?: number
  scrollSize?: number
  viewportSize?: number
}

interface UseChatScroll {
  onScroll: (event?: Event) => void
  onScrollerResize: () => void
  scrollToItem: (
    index: number | (() => number),
    forceAttemptsOrOptions?: boolean | ScrollToItemOptions,
  ) => Promise<number>
  scrollToBottom: (options?: ScrollToBottomOptions) => Promise<void>
  beginPrependLock: () => void
  endPrependLock: () => Promise<void>
  /** Keep scrollOffset while newer history appends (anchored mid-chat). */
  beginAppendLock: () => void
  endAppendLock: () => Promise<void>
  saveViewportForReuse: (resolveAnchorIndex?: (eventId: number) => number) => void
  /**
   * Restore mid-history keep-alive viewport, or signal `follow-tail` when the user
   * left pinned to the newest messages (caller applies unread/bottom rules).
   */
  restoreViewportAfterReuse: () => Promise<'restored' | 'follow-tail' | 'noop'>
  cancelStickToBottom: () => void
  checkHistoryLoadEdges: (force?: boolean) => void
  canLoadOlderHistory: () => boolean
  isPrependScrollLocked: DeepReadonly<Ref<boolean>>
  eventListContainer: Ref<HTMLElement | null>
  scroller: Ref<ScrollerInstance | null>
  isScrolledUp: DeepReadonly<Ref<boolean>>
}

const PRECISION_PX = 2
const RESIZE_HANDLING_THROTTLE_MS = 200
const RESIZE_HANDLING_MAX_ATTEMPTS = 5
const SCROLL_TO_BOTTOM_MAX_ATTEMPTS = RESIZE_HANDLING_MAX_ATTEMPTS * 4
const SCROLL_TO_BOTTOM_STABLE_CHECKS = 2
const PREPEND_LOCK_MAX_MS = 800
const PREPEND_LOCK_STABLE_FRAMES = 3
const REUSE_RESTORE_MAX_MS = 1200
const REUSE_RESTORE_STABLE_FRAMES = 6
const REUSE_RESTORE_MIN_MS = 200
const STICK_TO_BOTTOM_MS = 3000
/** Буфер над текущим вьюпортом: пачка истории должна встать выше видимых строк */
const NEAR_TOP_MIN_PX = 2200
const NEAR_TOP_VIEWPORT_FACTOR = 2.5
/** Жёсткий край списка — запасной load, если near-top не успел вставить */
const EDGE_LOAD_MAX_PX = 80

export function useChatScroll (options: UseChatScrollOptions = {}): UseChatScroll {
  const eventListContainer = ref<HTMLElement | null>(null)
  const scroller = ref<ScrollerInstance | null>(null)
  const isPrependScrollLocked = ref(false)
  const emitter = safeInject<Emitter<Events>>('emitter')
  const isScrolledUp = ref(false)
  let timerId: NodeJS.Timeout
  const scrollIntervalIds = new Set<NodeJS.Timeout>()
  /** Resolvers for in-flight scroll settle loops (item / bottom). */
  const activeSettleResolves = new Set<() => void>()
  /** Bumped to invalidate settle loops when the user takes over scrolling. */
  let programmaticScrollEpoch = 0
  let stickToBottomUntil = 0
  let stickToBottomTimer: ReturnType<typeof setTimeout> | null = null
  let wasNearTop = false
  let wasAtTopEdge = false
  let wasNearBottom = false
  let wasAtBottomEdge = false
  let prependLockSession = 0
  let prependLockRaf = 0
  let prependLockSafetyTimer: ReturnType<typeof setTimeout> | null = null
  let appendLockSession = 0
  let appendLockOffset: number | null = null
  let isAppendScrollLocked = false
  let reusedViewport: ReusedViewportSnapshot | null = null
  let lastLiveViewport: ReusedViewportSnapshot | null = null
  let resolveAnchorIndexForReuse: ((eventId: number) => number) | undefined
  let isReuseRestoreLocked = false
  let reuseRestoreSession = 0
  let reuseRestoreRaf = 0
  let reuseRestoreSafetyTimer: ReturnType<typeof setTimeout> | null = null
  let suppressLazyLoadEmit = false
  let isDisposed = false
  let lastEdgeScrollOffset = -1
  /** Blocks older-history load after the user scrolls toward the bottom. */
  let userScrollIntent: 'up' | 'down' | 'at-bottom' | null = null

  const resolveScrollerElement = (root: HTMLElement | null | undefined): HTMLElement | null => {
    if (!root) {
      return null
    }
    if (root.classList?.contains('wui-virtual-scroller')) {
      return root
    }
    return root.querySelector('.wui-virtual-scroller') ?? root
  }

  const getScrollContainer = (): HTMLElement | null => {
    return resolveScrollerElement(scroller.value?.$el)
      ?? resolveScrollerElement(eventListContainer.value)
  }

  /** Prefer virtua metrics — DOM scrollHeight desyncs during remeasure. */
  const getScrollMetrics = (): ScrollMetrics | null => {
    const ds = scroller.value
    if (
      ds
      && ds.scrollSize != null
      && ds.scrollOffset != null
      && ds.viewportSize != null
      && ds.viewportSize > 0
    ) {
      return {
        scrollOffset: ds.scrollOffset,
        scrollSize: ds.scrollSize,
        viewportSize: ds.viewportSize,
      }
    }

    const el = getScrollContainer()
    if (!el || el.clientHeight <= 0) {
      return null
    }

    return {
      scrollOffset: el.scrollTop,
      scrollSize: el.scrollHeight,
      viewportSize: el.clientHeight,
    }
  }

  const getNearTopThreshold = (viewportSize: number): number => {
    return Math.max(NEAR_TOP_MIN_PX, viewportSize * NEAR_TOP_VIEWPORT_FACTOR)
  }

  const getEdgeLoadThreshold = (viewportSize: number): number => {
    return Math.min(EDGE_LOAD_MAX_PX, getNearTopThreshold(viewportSize))
  }

  const distanceFromBottom = (metrics: ScrollMetrics): number => {
    return metrics.scrollSize - metrics.scrollOffset - metrics.viewportSize
  }

  const isNearBottomMetrics = (metrics: ScrollMetrics): boolean => {
    return distanceFromBottom(metrics) <= getEdgeLoadThreshold(metrics.viewportSize)
  }

  const isAtVirtuaBottom = (): boolean => {
    const metrics = getScrollMetrics()
    if (!metrics) {
      return false
    }
    return distanceFromBottom(metrics) <= PRECISION_PX
  }

  const canLoadOlderHistory = (): boolean => {
    // Plan: any down / at-bottom intent must block older-load.
    if (userScrollIntent === 'down' || userScrollIntent === 'at-bottom') {
      return false
    }

    const metrics = getScrollMetrics()
    if (!metrics) {
      return false
    }

    if (isNearBottomMetrics(metrics)) {
      return false
    }

    // Only while the user is actually near the top — prevents mid-list scrollStop loads.
    if (metrics.scrollOffset > getNearTopThreshold(metrics.viewportSize)) {
      return false
    }

    return true
  }

  const applyScrollToBottom = (force = false): void => {
    if ((isPrependScrollLocked.value || isReuseRestoreLocked) && !force) {
      return
    }
    scroller.value?.scrollToBottom?.()
    isScrolledUp.value = false
  }

  const cancelStickToBottom = (): void => {
    stickToBottomUntil = 0
    if (stickToBottomTimer) {
      clearTimeout(stickToBottomTimer)
      stickToBottomTimer = null
    }
    stickToBottomOnResize.cancel()
  }

  /** Stop settle intervals that keep re-applying scrollToItem/scrollToBottom. */
  const abortProgrammaticScrollSettling = (): void => {
    programmaticScrollEpoch += 1
    scrollIntervalIds.forEach((id) => clearInterval(id))
    scrollIntervalIds.clear()
    const resolvers = [...activeSettleResolves]
    activeSettleResolves.clear()
    resolvers.forEach((resolve) => resolve())
  }

  const isStickToBottomAllowed = (): boolean => {
    return options.canStickToBottom?.() !== false
  }

  const isStickToBottomActive = (): boolean => Date.now() < stickToBottomUntil

  const enableStickToBottom = (): void => {
    if (isPrependScrollLocked.value || !isStickToBottomAllowed()) {
      return
    }
    stickToBottomUntil = Date.now() + STICK_TO_BOTTOM_MS
    if (stickToBottomTimer) {
      clearTimeout(stickToBottomTimer)
    }
    stickToBottomTimer = setTimeout(() => {
      stickToBottomUntil = 0
      stickToBottomTimer = null
    }, STICK_TO_BOTTOM_MS)
  }

  const stickToBottomOnResize = throttle(() => {
    if (isPrependScrollLocked.value || !isStickToBottomActive()) {
      return
    }
    applyScrollToBottom()
  }, RESIZE_HANDLING_THROTTLE_MS)

  const clearPrependLockTimers = (): void => {
    if (prependLockRaf) {
      cancelAnimationFrame(prependLockRaf)
      prependLockRaf = 0
    }
    if (prependLockSafetyTimer) {
      clearTimeout(prependLockSafetyTimer)
      prependLockSafetyTimer = null
    }
  }

  const finishPrependLock = (): void => {
    clearPrependLockTimers()
    isPrependScrollLocked.value = false
    suppressLazyLoadEmit = true
    syncLazyLoadEdgeState()
  }

  const beginPrependLock = (): void => {
    cancelStickToBottom()
    clearPrependLockTimers()
    prependLockSession++
    isPrependScrollLocked.value = true
    suppressLazyLoadEmit = true
  }

  const endPrependLock = (): Promise<void> => {
    const session = prependLockSession

    return new Promise((resolve) => {
      const startedAt = Date.now()
      let lastScrollSize = -1
      let stableFrames = 0

      const finish = () => {
        if (session !== prependLockSession) {
          resolve()
          return
        }
        finishPrependLock()
        resolve()
      }

      prependLockSafetyTimer = setTimeout(finish, PREPEND_LOCK_MAX_MS)

      const tick = () => {
        if (isDisposed || session !== prependLockSession) {
          finish()
          return
        }

        const scrollSize = scroller.value?.scrollSize ?? -1
        if (scrollSize === lastScrollSize && scrollSize > 0) {
          stableFrames++
        } else {
          stableFrames = 0
          lastScrollSize = scrollSize
        }

        if (stableFrames >= PREPEND_LOCK_STABLE_FRAMES && Date.now() - startedAt >= 50) {
          finish()
          return
        }

        if (Date.now() - startedAt >= PREPEND_LOCK_MAX_MS) {
          finish()
          return
        }

        prependLockRaf = requestAnimationFrame(tick)
      }

      void nextTick().then(() => {
        if (isDisposed || session !== prependLockSession) {
          resolve()
          return
        }
        prependLockRaf = requestAnimationFrame(tick)
      })
    })
  }

  const beginAppendLock = (): void => {
    cancelStickToBottom()
    appendLockSession++
    isAppendScrollLocked = true
    suppressLazyLoadEmit = true
    appendLockOffset = getScrollMetrics()?.scrollOffset ?? scroller.value?.scrollOffset ?? null
  }

  const endAppendLock = (): Promise<void> => {
    const session = appendLockSession
    const lockedOffset = appendLockOffset

    const restoreOffset = (): void => {
      if (lockedOffset == null) {
        return
      }
      scroller.value?.scrollTo?.(lockedOffset)
    }

    return new Promise((resolve) => {
      const finish = () => {
        if (session !== appendLockSession) {
          resolve()
          return
        }
        restoreOffset()
        isAppendScrollLocked = false
        appendLockOffset = null
        suppressLazyLoadEmit = true
        syncLazyLoadEdgeState()
        resolve()
      }

      void nextTick().then(() => {
        if (isDisposed || session !== appendLockSession) {
          resolve()
          return
        }
        restoreOffset()
        requestAnimationFrame(() => {
          if (isDisposed || session !== appendLockSession) {
            resolve()
            return
          }
          restoreOffset()
          requestAnimationFrame(finish)
        })
      })
    })
  }

  const syncLazyLoadEdgeState = (): void => {
    const metrics = getScrollMetrics()
    if (!metrics) {
      lastEdgeScrollOffset = -1
      return
    }

    lastEdgeScrollOffset = metrics.scrollOffset
    const nearTopThreshold = getNearTopThreshold(metrics.viewportSize)
    const edgeLoadThreshold = getEdgeLoadThreshold(metrics.viewportSize)
    const fromBottom = distanceFromBottom(metrics)
    wasNearTop = metrics.scrollOffset <= nearTopThreshold
    wasAtTopEdge = metrics.scrollOffset <= edgeLoadThreshold
    wasNearBottom = fromBottom <= nearTopThreshold
    wasAtBottomEdge = fromBottom <= edgeLoadThreshold
  }

  const emitLazyLoadScrollEdges = (force = false, fromUserScroll = false): void => {
    const metrics = getScrollMetrics()
    if (
      !metrics
      || isPrependScrollLocked.value
      || isAppendScrollLocked
      || isReuseRestoreLocked
      || suppressLazyLoadEmit
    ) {
      return
    }

    const { scrollOffset, viewportSize } = metrics
    const nearTopThreshold = getNearTopThreshold(viewportSize)
    const edgeLoadThreshold = getEdgeLoadThreshold(viewportSize)
    const fromBottom = distanceFromBottom(metrics)
    const isNearTop = scrollOffset <= nearTopThreshold
    const isAtTopEdge = scrollOffset <= edgeLoadThreshold
    const isNearBottomZone = fromBottom <= nearTopThreshold
    const isAtBottomEdge = fromBottom <= edgeLoadThreshold

    const scrollingUp = lastEdgeScrollOffset >= 0 && scrollOffset < lastEdgeScrollOffset - PRECISION_PX
    const scrollingDown = lastEdgeScrollOffset >= 0 && scrollOffset > lastEdgeScrollOffset + PRECISION_PX

    const blocksTopHistoryLoad = userScrollIntent === 'down' || userScrollIntent === 'at-bottom'
    const enteringNearTop = fromUserScroll && isNearTop && !wasNearTop && scrollingUp
    const enteringTopEdge = fromUserScroll && isAtTopEdge && !wasAtTopEdge
    const shouldEmitTop = isNearTop
      && !blocksTopHistoryLoad
      && !scrollingDown
      && (
        (force && wasNearTop)
        || enteringNearTop
        || enteringTopEdge
        || (fromUserScroll && scrollingUp && isNearTop)
      )

    if (isNearTop && shouldEmitTop) {
      emitter.emit('scrolledNearTop')
    }

    if (isAtTopEdge && shouldEmitTop) {
      emitter.emit('scrolledToTop')
    }

    if (isNearBottomZone && (force || !wasNearBottom || (fromUserScroll && scrollingDown))) {
      emitter.emit('scrolledNearBottom')
    }

    if (isAtBottomEdge && (force || !wasAtBottomEdge || (fromUserScroll && scrollingDown))) {
      emitter.emit('scrolledToDown')
    }

    lastEdgeScrollOffset = scrollOffset
    wasNearTop = isNearTop
    wasAtTopEdge = isAtTopEdge
    wasNearBottom = isNearBottomZone
    wasAtBottomEdge = isAtBottomEdge
  }

  const onScrollThrottled = throttle((event?: Event) => {
    if (event?.isTrusted) {
      suppressLazyLoadEmit = false
    } else if (suppressLazyLoadEmit) {
      return
    }

    if (timerId) {
      clearTimeout(timerId)
    }

    emitter.emit('scrollStart')

    timerId = setTimeout(() => {
      emitter.emit('scrollStop')
    }, 150)

    const metrics = getScrollMetrics()
    if (metrics) {
      const { scrollOffset } = metrics
      const fromBottom = distanceFromBottom(metrics)
      const scrollingUp = lastEdgeScrollOffset >= 0 && scrollOffset < lastEdgeScrollOffset - PRECISION_PX
      const scrollingDown = lastEdgeScrollOffset >= 0 && scrollOffset > lastEdgeScrollOffset + PRECISION_PX

      if (event?.isTrusted) {
        if (scrollingDown) {
          userScrollIntent = 'down'
        } else if (scrollingUp) {
          const jumpPx = lastEdgeScrollOffset - scrollOffset
          // Remeasure can clamp offset downward while the user is still scrolling down.
          const isLikelyRemeasureGlitch = userScrollIntent === 'down'
            && jumpPx > getNearTopThreshold(metrics.viewportSize) * 0.25
          if (!isLikelyRemeasureGlitch) {
            userScrollIntent = 'up'
          }
        }
      }

      const scrolledUp = fromBottom > PRECISION_PX
      isScrolledUp.value = scrolledUp

      if (scrolledUp && event?.isTrusted) {
        cancelStickToBottom()
      }

      if (!isPrependScrollLocked.value && !isAppendScrollLocked && !isReuseRestoreLocked) {
        rememberLiveViewport()
        emitLazyLoadScrollEdges(false, Boolean(event?.isTrusted))
      }

      if (
        event?.isTrusted
        && fromBottom <= getEdgeLoadThreshold(metrics.viewportSize)
        && isStickToBottomAllowed()
      ) {
        if (userScrollIntent !== 'at-bottom') {
          userScrollIntent = 'at-bottom'
          emitter.emit('userAtChatBottom')
        }
        // Keep stick alive while the user is at the tail so remasure doesn't strand them above newest.
        enableStickToBottom()
      }
    }
  }, 200)

  const onScroll = (event?: Event): void => {
    // User took over — stop pin/quote settle loops that would snap back to the target.
    if (event?.isTrusted) {
      abortProgrammaticScrollSettling()
    }
    if (isReuseRestoreLocked) {
      applyReusedViewport()
      return
    }
    onScrollThrottled(event)
  }

  const onScrollerResize = () => {
    if (isPrependScrollLocked.value || isAppendScrollLocked || isReuseRestoreLocked) {
      return
    }
    stickToBottomOnResize()
    rememberLiveViewport()
    emitLazyLoadScrollEdges()
  }

  const rememberLiveViewport = (): void => {
    if (isReuseRestoreLocked || isPrependScrollLocked.value || isAppendScrollLocked) {
      return
    }
    const metrics = getScrollMetrics()
    const el = getScrollContainer()
    if (!metrics || !el) {
      return
    }
    // Identity only — which message is at the top. Scroll restore goes through virtua API.
    const anchor = findTopVisibleAnchor(el)
    const anchorIndex = anchor
      ? resolveAnchorIndexForReuse?.(anchor.eventId)
      : undefined
    lastLiveViewport = {
      scrollOffset: metrics.scrollOffset,
      wasAtBottom: distanceFromBottom(metrics) <= PRECISION_PX,
      ...(anchor?.eventId !== undefined ? { anchorEventId: anchor.eventId } : {}),
      ...(anchorIndex !== undefined && anchorIndex >= 0 ? { anchorIndex } : {}),
    }
  }

  const resolveReuseAnchorIndex = (snapshot: ReusedViewportSnapshot): number => {
    if (snapshot.anchorIndex !== undefined && snapshot.anchorIndex >= 0) {
      return snapshot.anchorIndex
    }
    if (snapshot.anchorEventId !== undefined && resolveAnchorIndexForReuse) {
      return resolveAnchorIndexForReuse(snapshot.anchorEventId)
    }
    return -1
  }

  const isReusedViewportAligned = (): boolean => {
    const snapshot = reusedViewport
    const ds = scroller.value
    if (!snapshot) {
      return true
    }

    const anchorIndex = resolveReuseAnchorIndex(snapshot)
    if (anchorIndex >= 0 && ds?.getItemOffset) {
      const expected = ds.getItemOffset(anchorIndex)
      const offset = ds.scrollOffset ?? 0
      return Math.abs(offset - expected) <= PRECISION_PX
    }

    const offset = ds?.scrollOffset ?? getScrollMetrics()?.scrollOffset ?? 0
    return Math.abs(offset - snapshot.scrollOffset) <= PRECISION_PX
  }

  const applyReusedViewport = (): void => {
    const snapshot = reusedViewport
    const ds = scroller.value
    if (!snapshot) {
      return
    }

    const anchorIndex = resolveReuseAnchorIndex(snapshot)
    if (anchorIndex >= 0) {
      ds?.scrollToItem?.(anchorIndex, { align: 'start' })
      return
    }

    if (ds?.scrollTo) {
      ds.scrollTo(snapshot.scrollOffset)
    }
  }

  const clearReuseRestoreTimers = (): void => {
    if (reuseRestoreRaf) {
      cancelAnimationFrame(reuseRestoreRaf)
      reuseRestoreRaf = 0
    }
    if (reuseRestoreSafetyTimer) {
      clearTimeout(reuseRestoreSafetyTimer)
      reuseRestoreSafetyTimer = null
    }
  }

  const finishReuseRestore = (): void => {
    reuseRestoreSession++
    clearReuseRestoreTimers()
    isReuseRestoreLocked = false
    suppressLazyLoadEmit = true
    syncLazyLoadEdgeState()
  }

  const saveViewportForReuse = (resolveAnchorIndex?: (eventId: number) => number): void => {
    cancelStickToBottom()
    clearReuseRestoreTimers()
    resolveAnchorIndexForReuse = resolveAnchorIndex
    rememberLiveViewport()
    reusedViewport = lastLiveViewport
    isReuseRestoreLocked = true
  }

  const discardReuseViewport = (): void => {
    reusedViewport = null
    finishReuseRestore()
  }

  const restoreViewportAfterReuse = (): Promise<'restored' | 'follow-tail' | 'noop'> => {
    if (!reusedViewport || isDisposed) {
      return Promise.resolve('noop')
    }

    // Left at the tail — caller should apply unread/bottom rules instead of mid-list anchor.
    if (reusedViewport.wasAtBottom) {
      discardReuseViewport()
      return Promise.resolve('follow-tail')
    }

    cancelStickToBottom()
    clearReuseRestoreTimers()
    isReuseRestoreLocked = true
    const session = ++reuseRestoreSession

    applyReusedViewport()

    return new Promise((resolve) => {
      const startedAt = Date.now()
      let lastScrollSize = -1
      let stableFrames = 0

      const finish = () => {
        if (session !== reuseRestoreSession) {
          resolve('restored')
          return
        }
        applyReusedViewport()
        finishReuseRestore()
        resolve('restored')
      }

      reuseRestoreSafetyTimer = setTimeout(finish, REUSE_RESTORE_MAX_MS)

      const tick = () => {
        if (isDisposed || session !== reuseRestoreSession) {
          finish()
          return
        }

        applyReusedViewport()

        const metrics = getScrollMetrics()
        if (metrics) {
          const isAligned = isReusedViewportAligned()
          const scrollSize = metrics.scrollSize
          if (isAligned && scrollSize === lastScrollSize) {
            stableFrames++
          } else {
            stableFrames = 0
            lastScrollSize = scrollSize
          }

          if (
            isAligned
            && stableFrames >= REUSE_RESTORE_STABLE_FRAMES
            && Date.now() - startedAt >= REUSE_RESTORE_MIN_MS
          ) {
            finish()
            return
          }
        }

        if (Date.now() - startedAt >= REUSE_RESTORE_MAX_MS) {
          finish()
          return
        }

        reuseRestoreRaf = requestAnimationFrame(tick)
      }

      void nextTick().then(() => {
        if (isDisposed || session !== reuseRestoreSession) {
          resolve('restored')
          return
        }
        return nextTick()
      }).then(() => {
        if (isDisposed || session !== reuseRestoreSession) {
          resolve('restored')
          return
        }
        applyReusedViewport()
        reuseRestoreRaf = requestAnimationFrame(tick)
      })
    })
  }

  const scrollToBottom = (options?: ScrollToBottomOptions): Promise<void> => {
    if (isDisposed) {
      return Promise.resolve()
    }

    // Anchored mid-chat: list end is not the real tail — never stick/jump there.
    if (options?.force !== true && !isStickToBottomAllowed()) {
      return Promise.resolve()
    }

    if (options?.force) {
      if (isPrependScrollLocked.value) {
        finishPrependLock()
      }
      if (options.stick !== false) {
        enableStickToBottom()
      }
      applyScrollToBottom(true)
      return Promise.resolve()
    }

    if (isPrependScrollLocked.value || isAppendScrollLocked) {
      return Promise.resolve()
    }

    const shouldStick = options?.stick !== false
    const shouldMeasure = options?.measure !== false

    if (!shouldStick && !shouldMeasure) {
      applyScrollToBottom()
      return Promise.resolve()
    }

    if (shouldStick) {
      enableStickToBottom()
    }

    if (options?.settle === false) {
      applyScrollToBottom()
      return Promise.resolve()
    }

    abortProgrammaticScrollSettling()
    const epoch = programmaticScrollEpoch

    return new Promise((resolve) => {
      let attempts = 0
      let stableChecks = 0
      let lastScrollSize = -1
      let settled = false

      const finish = (): void => {
        if (settled) {
          return
        }
        settled = true
        clearInterval(intervalId)
        scrollIntervalIds.delete(intervalId)
        activeSettleResolves.delete(finish)
        resolve()
      }
      activeSettleResolves.add(finish)

      const intervalId = setInterval(() => {
        if (isDisposed || epoch !== programmaticScrollEpoch) {
          finish()
          return
        }
        attempts++

        applyScrollToBottom()

        const scrollSize = scroller.value?.scrollSize ?? -1
        if (scrollSize === lastScrollSize && isAtVirtuaBottom()) {
          stableChecks++
        } else {
          stableChecks = 0
          lastScrollSize = scrollSize
        }

        if (stableChecks >= SCROLL_TO_BOTTOM_STABLE_CHECKS || attempts >= SCROLL_TO_BOTTOM_MAX_ATTEMPTS) {
          finish()
        }
      }, RESIZE_HANDLING_THROTTLE_MS)
      scrollIntervalIds.add(intervalId)
    })
  }

  const isIndexAlignedToTop = (index: number): boolean => {
    const ds = scroller.value
    if (!ds?.getItemOffset || ds.scrollOffset == null) {
      return false
    }
    return Math.abs(ds.scrollOffset - ds.getItemOffset(index)) <= PRECISION_PX
  }

  /** Height from item start to list end — used to decide top-anchor vs bottom pin. */
  const getRemainingHeightFromIndex = (index: number): number | null => {
    const ds = scroller.value
    if (!ds?.getItemOffset || ds.scrollSize == null || ds.scrollSize <= 0) {
      return null
    }
    return Math.max(0, ds.scrollSize - ds.getItemOffset(index))
  }

  const shouldPinItemToBottom = (index: number): boolean => {
    const ds = scroller.value
    const remaining = getRemainingHeightFromIndex(index)
    const viewport = ds?.viewportSize ?? 0
    if (remaining == null || viewport <= 0) {
      return false
    }
    return remaining <= viewport + PRECISION_PX
  }

  const resolveScrollToItemOptions = (
    forceAttemptsOrOptions: boolean | ScrollToItemOptions = false,
  ): Required<Pick<ScrollToItemOptions, 'forceAttempts' | 'bottomIfFits'>> & Pick<ScrollToItemOptions, 'align'> => {
    if (typeof forceAttemptsOrOptions === 'boolean') {
      return {
        forceAttempts: forceAttemptsOrOptions,
        bottomIfFits: false,
        align: 'start',
      }
    }
    return {
      forceAttempts: forceAttemptsOrOptions.forceAttempts ?? false,
      bottomIfFits: forceAttemptsOrOptions.bottomIfFits ?? false,
      align: forceAttemptsOrOptions.align ?? 'start',
    }
  }

  const applyScrollToIndex = (index: number, align: ScrollToItemAlign['align'], bottomIfFits: boolean): void => {
    const ds = scroller.value
    if (index < 0 || !ds) {
      return
    }
    if (bottomIfFits && shouldPinItemToBottom(index)) {
      ds.scrollToBottom?.()
      isScrolledUp.value = false
      return
    }
    ds.scrollToItem?.(index, { align: align ?? 'start' })
  }

  const scrollToItem = (
    indexOrResolve: number | (() => number),
    forceAttemptsOrOptions: boolean | ScrollToItemOptions = false,
  ): Promise<number> => {
    const {
      forceAttempts,
      bottomIfFits,
      align,
    } = resolveScrollToItemOptions(forceAttemptsOrOptions)

    if (isDisposed) {
      return Promise.resolve(typeof indexOrResolve === 'function' ? indexOrResolve() : indexOrResolve)
    }
    if (forceAttempts && isPrependScrollLocked.value) {
      finishPrependLock()
    }

    if (isPrependScrollLocked.value && !forceAttempts) {
      return Promise.resolve(typeof indexOrResolve === 'function' ? indexOrResolve() : indexOrResolve)
    }

    if (forceAttempts) {
      abortProgrammaticScrollSettling()
    }

    const resolveIndex = (): number => (
      typeof indexOrResolve === 'function' ? indexOrResolve() : indexOrResolve
    )

    let index = resolveIndex()
    if (index >= 0) {
      applyScrollToIndex(index, align, bottomIfFits)
    }

    if (!forceAttempts) {
      nextTick(() => {
        const latest = resolveIndex()
        if (latest >= 0) {
          applyScrollToIndex(latest, align, bottomIfFits)
        }
      })
      return Promise.resolve(index)
    }

    const epoch = programmaticScrollEpoch
    const maxAttempts = RESIZE_HANDLING_MAX_ATTEMPTS * 8
    return new Promise((resolve) => {
      let attempts = 0
      let stableChecks = 0
      let lastOffset = Number.NaN
      let settled = false

      const finish = (value: number): void => {
        if (settled) {
          return
        }
        settled = true
        clearInterval(timerId)
        scrollIntervalIds.delete(timerId)
        activeSettleResolves.delete(onAbort)
        resolve(value)
      }
      const onAbort = (): void => finish(resolveIndex())
      activeSettleResolves.add(onAbort)

      const timerId = setInterval(() => {
        if (isDisposed || epoch !== programmaticScrollEpoch) {
          finish(resolveIndex())
          return
        }
        attempts++

        index = resolveIndex()
        if (index < 0) {
          if (attempts >= maxAttempts) {
            finish(index)
          }
          return
        }

        const current = scroller.value
        const pinToBottom = bottomIfFits && shouldPinItemToBottom(index)
        applyScrollToIndex(index, align, bottomIfFits)

        const offset = current?.scrollOffset ?? Number.NaN
        const aligned = pinToBottom
          ? isAtVirtuaBottom()
          : isIndexAlignedToTop(index)

        if (aligned && Number.isFinite(offset) && Math.abs(offset - lastOffset) <= PRECISION_PX) {
          stableChecks++
        } else {
          stableChecks = 0
          lastOffset = offset
        }

        if (stableChecks >= 3 || attempts >= maxAttempts) {
          finish(index)
        }
      }, RESIZE_HANDLING_THROTTLE_MS)
      scrollIntervalIds.add(timerId)
    })
  }

  const checkHistoryLoadEdges = (force = false): void => {
    emitLazyLoadScrollEdges(force)
    // Older insert listens to scrollStop (not mid-gesture near-top). Cooldown/force
    // still needs an insert trigger while the user remains near the top.
    if (!force || isPrependScrollLocked.value || isReuseRestoreLocked) {
      return
    }
    const metrics = getScrollMetrics()
    if (!metrics) {
      return
    }
    if (metrics.scrollOffset <= getNearTopThreshold(metrics.viewportSize)) {
      emitter.emit('scrollStop')
    }
  }

  onScopeDispose(() => {
    isDisposed = true
    finishPrependLock()
    isAppendScrollLocked = false
    appendLockOffset = null
    if (timerId) {
      clearTimeout(timerId)
    }
    if (stickToBottomTimer) {
      clearTimeout(stickToBottomTimer)
    }
    onScrollThrottled.cancel()
    stickToBottomOnResize.cancel()
    scrollIntervalIds.forEach((id) => clearInterval(id))
    scrollIntervalIds.clear()
    activeSettleResolves.clear()
    scroller.value = null
    eventListContainer.value = null
    reusedViewport = null
    finishReuseRestore()
    wasNearTop = false
    wasAtTopEdge = false
    wasNearBottom = false
    wasAtBottomEdge = false
    lastEdgeScrollOffset = -1
    userScrollIntent = null
  })

  return {
    onScroll,
    onScrollerResize,
    eventListContainer,
    scroller,
    isScrolledUp,
    isPrependScrollLocked: readonly(isPrependScrollLocked),
    scrollToItem,
    scrollToBottom,
    beginPrependLock,
    endPrependLock,
    beginAppendLock,
    endAppendLock,
    saveViewportForReuse,
    restoreViewportAfterReuse,
    cancelStickToBottom,
    checkHistoryLoadEdges,
    canLoadOlderHistory,
  }
}
